import React, { useEffect, useRef, useState } from "react";
import {
  CameraFill,
  ChevronLeft,
  PersonCircle,
  PlusLg,
  Shop,
  Trash,
} from "react-bootstrap-icons";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  createStoreOwner,
  getStoreOwnerDetails,
  updateStoreOwner,
} from "../../services/storeOwnerServices";
import { decryptData, encryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { useTranslation } from "react-i18next";
import {
  getCityList,
  getCountryList,
  getStateList,
} from "../../services/commonServices";
import Select from "react-select";
import { customStyles } from "../../utils/function";
import { notify } from "../../components/Common/Alerts";
import { useConfig } from "../../components/Context/ConfigContext";

const AddStoreOwner = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const inputRef = useRef(null);
  const { t } = useTranslation();
  const { configData } = useConfig();
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark",
  );

  const [formData, setFormData] = useState({
    full_name: "",
    mobile: "",
    email: "",
    store_name: "",
    store_mobile: "",
    store_business_type: "",
    store_address: "",
    store_landmark: "",
    store_city_id: "",
    store_state_id: "",
    store_country_id: "",
    store_pincode: "",
    store_latitude: "",
    store_longitude: "",
    uuid: "",
    is_store: 0,
  });
  const storeFields = [
    "store_name",
    "store_mobile",
    "store_business_type",
    "store_address",
    "store_landmark",
    "store_city_id",
    "store_state_id",
    "store_country_id",
    "store_pincode",
    "store_latitude",
    "store_longitude",
  ];
  const [errors, setErrors] = useState({
    full_name: "",
    mobile: "",
    email: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [storeOwnerDetails, setStoreOwnerDetails] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem("theme") === "dark");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };
  const handleDelete = (e) => {
    e.stopPropagation();
    setFile(null);
    setPreview(null);
  };

  useEffect(() => {
    const isAnyStoreFieldFilled = storeFields
      .filter((field) => !["store_latitude", "store_longitude"].includes(field))
      .some((field) => {
        const value = formData?.[field];

        if (value === null || value === undefined) return false;

        if (typeof value === "string") return value.trim() !== "";

        if (typeof value === "number") return value > 0;

        return false;
      });

    setFormData((prev) => {
      const newValue = isAnyStoreFieldFilled ? 1 : 0;

      if (prev.is_store === newValue) return prev;

      return {
        ...prev,
        is_store: newValue,
      };
    });
  }, [
    formData.store_name,
    formData.store_mobile,
    formData.store_business_type,
    formData.store_address,
    formData.store_landmark,
    formData.store_city_id,
    formData.store_state_id,
    formData.store_country_id,
    formData.store_pincode,
  ]);

  useEffect(() => {
    if (uuid) return;
    if (!window.google) return;

    const hasSavedLocation =
      formData?.store_latitude && formData?.store_longitude;

    const initMap = (lat, lng) => {
      const mapInstance = new window.google.maps.Map(
        document.getElementById("map"),
        {
          center: { lat, lng },
          zoom: 15,
        },
      );

      const markerInstance = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapInstance,
        draggable: true,
      });

      setMap(mapInstance);
      setMarker(markerInstance);

      markerInstance.addListener("dragend", () => {
        const pos = markerInstance.getPosition();

        const newLat = pos.lat();
        const newLng = pos.lng();

        setFormData((prev) => ({
          ...prev,
          store_latitude: newLat,
          store_longitude: newLng,
        }));

        getAddressFromLatLng(newLat, newLng);
      });
    };

    if (hasSavedLocation) {
      initMap(
        Number(formData.store_latitude),
        Number(formData.store_longitude),
      );
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setFormData((prev) => ({
            ...prev,
            store_latitude: lat,
            store_longitude: lng,
          }));

          initMap(lat, lng);
        },
        (error) => {
          console.log("GPS error:", error);

          initMap(23.0225, 72.5714);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      initMap(23.0225, 72.5714);
    }
  }, [formData?.store_latitude, formData?.store_longitude]);

  const getAddressFromLatLng = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results[0]) return;

      const result = results[0];

      let address = "";
      let cityName = "";
      let stateName = "";
      let countryName = "";
      let pincode = "";
      let landmark = "";

      result.address_components.forEach((component) => {
        const types = component.types;

        if (types.includes("postal_code")) {
          pincode = component.long_name;
        }

        if (
          types.includes("locality") ||
          types.includes("administrative_area_level_2")
        ) {
          cityName = component.long_name;
        }

        if (types.includes("administrative_area_level_1")) {
          stateName = component.long_name;
        }

        if (types.includes("country")) {
          countryName = component.long_name;
        }

        if (
          types.includes("sublocality_level_1") ||
          types.includes("sublocality_level_2") ||
          types.includes("neighborhood") ||
          types.includes("premise")
        ) {
          landmark = component.long_name;
        }
      });

      address = result.formatted_address.split(",").slice(0, 2).join(", ");

      const countryObj = countryList.find((c) => c.label === countryName);

      const stateObj = stateList.find((s) => s.label === stateName);

      const cityObj = cityList.find((c) => c.label === cityName);

      setFormData((prev) => ({
        ...prev,

        store_address: address,
        store_landmark: landmark || cityName,
        store_pincode: pincode,

        // dropdown fields (IDs only)
        store_country_id: countryObj?.value ? String(countryObj.value) : "",
        store_state_id: stateObj?.value ? String(stateObj.value) : "",
        store_city_id: cityObj?.value ? String(cityObj.value) : "",

        // optional lat/lng
        store_latitude: lat,
        store_longitude: lng,
      }));
    });
  };

  const getLatLngFromAddress = (address) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;

        const lat = location.lat();
        const lng = location.lng();

        setFormData((prev) => ({
          ...prev,
          store_latitude: lat,
          store_longitude: lng,
        }));

        if (map) {
          map.setCenter({ lat, lng });
        }

        if (marker) {
          marker.setPosition({ lat, lng });
        }
      }
    });
  };
  const storeActive = formData.is_store === 1;

  const handleMobileValidation = (fieldName = "mobile") => {
    if (fieldName === "store_mobile" && !storeActive) {
      setErrors((prev) => ({
        ...prev,
        store_mobile: "",
      }));
      return;
    }

    let errorMsg = "";
    const mobileRegex = /^\d{10}$/;
    const value = formData[fieldName];

    if (!value) {
      errorMsg = "Mobile is required";
    } else if (!mobileRegex.test(value)) {
      errorMsg = "Enter a valid 10-digit mobile number";
    }

    setErrors((prev) => ({
      ...prev,
      [fieldName]: errorMsg,
    }));
  };

  const validateField = (name, value) => {
    const storeActive = formData.is_store === 1;

    if (storeFields.includes(name)) {
      if (!storeActive) return ""; //store closed = no validation

      const stringValue = String(value ?? "").trim();

      if (!stringValue) {
        return "This field is required";
      }

      return "";
    }

    switch (name) {
      case "full_name":
        if (!value) return t("nameIsRequired");
        return "";

      case "mobile":
        if (!value) return t("mobileIsRequired");
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return t("enterDigitMobileNumber");
        }
        return "";

      case "email":
        if (!value?.trim()) return t("emailIsRequired");
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) return t("enterValidEmail");
        return "";

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          return t("pleasEnterEmail");
        }
        return "";

      default:
        return "";
    }
  };

  const isFieldRequired = (name, formData) => {
    return storeFields.includes(name) && formData.is_store === 1;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const cleanedValue = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        mobile: cleanedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value, { ...formData, [name]: value }),
    }));
  };

  useEffect(function () {
    getCountryList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setCountryList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (formData?.store_country_id) {
      let data = {
        country_id: formData.store_country_id,
      };

      getStateList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);

          const formatted = decrypted.data.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setStateList(formatted);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [formData?.store_country_id]);

  useEffect(() => {
    if (formData?.store_state_id) {
      let data = {
        state_id: formData.store_state_id,
      };

      getCityList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);

          const formatted = decrypted.data.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setCityList(formatted);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [formData?.store_state_id]);

  useEffect(
    function () {
      if (uuid) {
        let data = {
          uuid: uuid,
        };
        getStoreOwnerDetails(data)
          .then((response) => {
            const decrypted = decryptData(response.data.data);
            setStoreOwnerDetails(decrypted.data);
          })
          .catch((err) => {
            navigate("/owner-store");
            notify.error(Utils.getErrorMessage(err));
          });
      }
    },
    [navigate, uuid],
  );

  useEffect(
    function () {
      if (storeOwnerDetails) {
        setFormData({
          full_name: storeOwnerDetails?.full_name,
          mobile: storeOwnerDetails?.mobile,
          email: storeOwnerDetails?.email,
          store_name: storeOwnerDetails?.store_name,
          store_mobile: storeOwnerDetails?.store_mobile,
          store_business_type: storeOwnerDetails?.store_business_type,
          store_address: storeOwnerDetails?.store_address,
          store_landmark: storeOwnerDetails?.store_landmark,
          store_city_id: storeOwnerDetails?.store_city_id,
          store_state_id: storeOwnerDetails?.store_state_id,
          store_country_id: storeOwnerDetails?.store_country_id,
          store_pincode: storeOwnerDetails?.store_pincode,
          store_longitude: storeOwnerDetails?.store_longitude,
          store_latitude: storeOwnerDetails?.store_latitude,
          uuid: storeOwnerDetails?.uuid,
        });
        setFile(storeOwnerDetails?.profile_image);
        setPreview(storeOwnerDetails?.profile_image);
      }
    },
    [storeOwnerDetails],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      full_name: validateField("full_name", formData.full_name),
      mobile: validateField("mobile", formData.mobile),
      email: validateField("email", formData.email),

      ...storeFields.reduce((acc, field) => {
        if (uuid) return acc;
        const error = validateField(field, formData[field], formData);
        if (error) acc[field] = error;
        return acc;
      }, {}),
    };
    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);

    const isAnyStoreFieldFilled = storeFields.some((field) => {
      const value = formData?.[field];
      return String(value ?? "").trim() !== "";
    });

    // if (isAnyStoreFieldFilled && !formData.is_store && !uuid) {
    //   notify.error("Please enable 'Switch Store' to submit store details");
    //   return;
    // }

    if (hasErrors) return;

    setBtnLoading(true);

    const data = {
      full_name: formData.full_name?.trim() || "",
      mobile: formData.mobile || "",
      email: formData.email?.trim() || "",
      store_name: formData.store_name?.trim() || "",
      store_mobile: formData.store_mobile || "",
      store_business_type: formData.store_business_type || "",
      store_address: formData.store_address?.trim() || "",
      store_landmark: formData.store_landmark?.trim() || "",
      store_city_id: formData.store_city_id || "",
      store_state_id: formData.store_state_id || "",
      store_country_id: formData.store_country_id || "",
      store_pincode: formData.store_pincode || "",
      store_latitude: formData.store_latitude || "",
      store_longitude: formData.store_longitude || "",
      is_store:formData.is_store,
      is_delete_icon: file ? 0 : 1,

      ...(formData.uuid && { uuid: formData.uuid }),
    };
    let q = encryptData(data);

    const formPayload = new FormData();

    if (file) {
      formPayload.append("files", file);
    }
    formPayload.append("q", q);
    const apiCall = formData?.uuid ? updateStoreOwner : createStoreOwner;

    try {
      const response = await apiCall(formPayload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setFormData({
        full_name: "",
        mobile: "",
        email: "",
        store_name: "",
        store_mobile: "",
        store_business_type: "",
        store_address: "",
        store_landmark: "",
        store_city_id: "",
        store_state_id: "",
        store_country_id: "",
        store_pincode: "",
        store_latitude: "",
        store_longitude: "",
        uuid: "",
      });
      setErrors("");
      navigate("/owner-store");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const businessOptions = configData?.business_type || [];

  const selectedOption =
    businessOptions.find((opt) => opt.value === formData.store_business_type) ||
    null;

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div>
      <div className="mx-auto space-y-8 animate-fade-up">
        <div className="hidden md:flex flex items-center gap-4 md:gap-6 flex-shrink-0 px-2 mb-2">
          <button
            onClick={() => navigate("/owner-store")}
            className="md:hidden group flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
          >
            <ChevronLeft
              size={18}
              className="group-hover:-translate-x-1 transition-transform md:hidden"
            />
            <span className="hidden ml-2 font-bold text-sm">{t("back")}</span>
          </button>

          <div className="flex-1">
            <h2 className="text-xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
              {uuid ? t("updateNewStoreOwner") : t("addNewStoreOwner")}{" "}
            </h2>
            <p className="hidden md:block text-gray-400 font-medium text-sm">
              {t("setupPartnerProfile")}
            </p>
          </div>
        </div>
        <section className="bg-white dark:bg-slate-900 rounded-[40px] border border-gray-100 p-8 lg:p-12 shadow-sm">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-12 h-12 bg-brand-orange/10 dark:bg-brand-orange/20 text-brand-orange dark:text-brand-orange rounded-2xl flex items-center justify-center">
              <PersonCircle size={24} />
            </div>
            <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">
              {t("storeOwnerInformation")}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-1 flex flex-col items-center">
              <div
                className="relative group cursor-pointer"
                onClick={() => inputRef.current.click()}
              >
                <input
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />

                <div className="w-36 h-36 bg-gray-50 rounded-full border-4 border-white shadow-xl flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CameraFill size={32} className="text-gray-200" />
                  )}
                </div>

                {!file && (
                  <div className="absolute bottom-2 right-2 bg-brand-orange text-white p-2.5 rounded-full border-4 border-white shadow-lg">
                    <PlusLg size={16} />
                  </div>
                )}

                {file && (
                  <div
                    onClick={handleDelete}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full border-4 border-white shadow-lg"
                  >
                    <Trash size={14} />
                  </div>
                )}
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-6">
                {t("profilePicture")}
              </p>
            </div>

            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="input-label">
                  {t("ownerName")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  placeholder={t("enterYourOwnerName")}
                  className={`input-field ${errors.full_name ? "input-error" : ""}`}
                  value={formData?.full_name}
                  onChange={handleChange}
                />
                {errors.full_name && (
                  <span className="text-danger">{errors.full_name}</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="input-label">
                  {t("mobileNumber")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="mobile"
                  placeholder={t("enterYourMobileNumber")}
                  className={`input-field ${errors.mobile ? "input-error" : ""}`}
                  value={formData?.mobile}
                  onChange={handleChange}
                  onBlur={() => handleMobileValidation("mobile")}
                  min={0}
                  onKeyDown={(e) => {
                    if (["-", "e", "E", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.mobile && (
                  <span className={`text-danger`}>{errors.mobile}</span>
                )}
              </div>

              <div className="flex flex-col gap-2 md:col-span-2">
                <label htmlFor="name" className="input-label">
                  {t("email")}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder={t("enterYourEmail")}
                  className={`input-field ${errors.email ? "input-error" : ""}`}
                  value={formData?.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <span className={`text-danger`}>{errors.email}</span>
                )}
              </div>
            </div>
            {uuid && (
              <div className="col-span-full flex items-end justify-end gap-4 mb-20 lg:mb-0">
                {" "}
                <button
                  className="btn-filter-close"
                  onClick={() => {
                    navigate("/owner-store");
                  }}
                >
                  {t("back")}
                </button>
                <button
                  type="button"
                  form="category-form"
                  className="btn-submit-orange flex items-center gap-2 mb-1"
                  onClick={handleSubmit}
                  disabled={btnLoading}
                >
                  {btnLoading && (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  )}
                  {btnLoading
                    ? t("submitting")
                    : formData.uuid
                      ? t("update")
                      : t("submit")}
                </button>
              </div>
            )}
          </div>
        </section>

        {!uuid && (
          <section className="bg-white dark:bg-slate-900 rounded-[40px] border border-gray-100 p-8 lg:p-12 shadow-sm">
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-orange/10 text-brand-orange rounded-2xl flex items-center justify-center">
                  <Shop size={24} />
                </div>
                <h2 className="text-xl font-black text-slate-800">
                  {t("storeDetails")}{" "}
                  <span className="text-gray-300 font-medium text-sm">
                    ({t("optional")})
                  </span>
                </h2>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={formData.is_store === 1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_store: e.target.checked ? 1 : 0,
                    }))
                  }
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-orange/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-gradient shadow-inner"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="store_name" className="input-label">
                  {t("storeName")}{" "}
                  {isFieldRequired("store_name", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  type="text"
                  name="store_name"
                  placeholder={t("enterStoreName")}
                  className={`input-field ${errors.store_name ? "input-error" : ""}`}
                  value={formData?.store_name || ""}
                  onChange={handleChange}
                />
                {storeActive && errors.store_name && (
                  <span className="text-danger">{errors.store_name}</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="store_mobile" className="input-label">
                  {t("mobileNumber")}
                  {isFieldRequired("store_mobile", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="store_mobile"
                  placeholder={t("enterMobileNumber")}
                  value={formData?.store_mobile || ""}
                  onChange={handleChange}
                  className={`input-field ${errors.store_mobile ? "input-error" : ""}`}
                  min={0}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={() => handleMobileValidation("store_mobile")}
                />{" "}
                {storeActive && errors.store_mobile && (
                  <span className="text-danger">{errors.store_mobile}</span>
                )}
              </div>

              <div className="flex flex-col gap-2 lg:col-span-3">
                <label htmlFor="store_address" className="input-label">
                  {t("address")}
                  {isFieldRequired("store_address", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <textarea
                  type="text"
                  name="store_address"
                  placeholder={t("enterYourAddress")}
                  className={`input-field ${errors.store_address ? "input-error" : ""}`}
                  value={formData?.store_address || ""}
                  onChange={handleChange}
                  rows={3}
                  onBlur={(e) => getLatLngFromAddress(e.target.value)}
                />
                {storeActive && errors.store_address && (
                  <span className="text-danger">{errors.store_address}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="store_landmark" className="input-label">
                  {t("landmark")}
                  {isFieldRequired("store_landmark", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>

                <input
                  type="text"
                  name="store_landmark"
                  placeholder={t("enterYourLandmark")}
                  className={`input-field ${errors.store_landmark ? "input-error" : ""}`}
                  value={formData?.store_landmark || ""}
                  onChange={handleChange}
                />
                {storeActive && errors.store_landmark && (
                  <span className="text-danger">
                    {errors.store_landmark}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="store_pincode" className="input-label">
                  {t("pincode")}
                  {isFieldRequired("store_pincode", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="store_pincode"
                  placeholder={t("enteryourPincode")}
                  className={`input-field ${errors.store_pincode ? "input-error" : ""}`}
                  value={formData?.store_pincode || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);

                    setFormData((prev) => ({
                      ...prev,
                      store_pincode: value,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      store_pincode: validateField("store_pincode", value, {
                        ...formData,
                        store_pincode: value,
                      }),
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
                {storeActive && errors.store_pincode && (
                  <span className="text-danger">{errors.store_pincode}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="store_country_id" className="input-label">
                  {t("country")}
                  {isFieldRequired("store_country_id", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>

                <Select
                  options={countryList}
                  value={getSelectedOption(
                    countryList,
                    formData.store_country_id,
                  )}
                  onChange={(selected) => {
                    setFormData((prev) => ({
                      ...prev,
                      store_country_id: String(selected?.value ?? ""),
                      store_state_id: "",
                      store_city_id: "",
                    }));
                    setStateList([]);
                    setCityList([]);
                  }}
                  styles={customStyles(darkMode)}
                  placeholder={t("selectCountry")}
                  isClearable={true}
                  isSearchable={true}
                />
                {storeActive && errors.store_country_id && (
                  <span className="text-danger">{errors.store_country_id}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="store_state_id" className="input-label">
                  {t("state")}
                  {isFieldRequired("store_state_id", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <Select
                  options={stateList}
                  value={getSelectedOption(stateList, formData.store_state_id)}
                  onChange={(selected) => {
                    setFormData((prev) => ({
                      ...prev,
                      store_state_id: String(selected?.value ?? ""),
                      store_city_id: "",
                    }));
                    setCityList([]);
                  }}
                  styles={customStyles(darkMode)}
                  placeholder={t("selectState")}
                  isClearable={true}
                  isSearchable={true}
                />
                {storeActive && errors.store_state_id && (
                  <span className="text-danger">{errors.store_state_id}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="store_city_id" className="input-label">
                  {t("city")}
                  {isFieldRequired("store_city_id", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <Select
                  options={cityList}
                  value={getSelectedOption(cityList, formData.store_city_id)}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      store_city_id: String(selected?.value ?? ""),
                    }))
                  }
                  styles={customStyles(darkMode)}
                  placeholder={t("selectCity")}
                  isClearable={true}
                  isSearchable={true}
                />
                {storeActive && errors.store_city_id && (
                  <span className="text-danger">{errors.store_city_id}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="store_business_type" className="input-label">
                  {t("businessType")}
                  {isFieldRequired("store_business_type", formData) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <Select
                  id="business-type"
                  options={businessOptions}
                  value={selectedOption}
                  onChange={(selected) => {
                    setFormData((prev) => ({
                      ...prev,
                      store_business_type: selected ? selected.value : "",
                    }));
                  }}
                  styles={customStyles(darkMode)}
                  placeholder={t("selectBusinessType")}
                  isClearable={true}
                  isSearchable={true}
                />
                {storeActive && errors.store_business_type && (
                  <span className="text-danger">
                    {errors.store_business_type}
                  </span>
                )}
              </div>
              <div className="h-[300px] rounded-[24px] overflow-hidden border-2 border-dashed border-slate-200">
                <div id="map" className="w-full h-full" />
              </div>

              <div className="col-span-full flex items-end justify-end gap-4 mb-20 lg:mb-0">
                {" "}
                <button
                  className="btn-filter-close"
                  onClick={() => {
                    navigate("/owner-store");
                  }}
                >
                  {t("back")}
                </button>
                <button
                  type="button"
                  form="category-form"
                  className="btn-submit-orange flex items-center gap-2 mb-1"
                  onClick={handleSubmit}
                  disabled={btnLoading}
                >
                  {btnLoading && (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  )}
                  {btnLoading
                    ? t("submitting")
                    : formData.uuid
                      ? t("update")
                      : t("submit")}
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AddStoreOwner;
